/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import type { ChipProps } from '@mui/material';

import type {
  ModelLabDeploymentStatus,
  ModelLabTrainingStatus,
  ModelLabVersionStage,
} from '../../types';

export function parseJsonInput<T>(value: string, fallback: T): T {
  const trimmed = value.trim();
  if (!trimmed) {
    return fallback;
  }

  return JSON.parse(trimmed) as T;
}

export function parseCommaList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

export function formatMetricSummary(
  metrics?: Record<string, any> | null
): string[] {
  if (!metrics) {
    return [];
  }

  return Object.entries(metrics)
    .filter(([, value]) => value != null && typeof value !== 'object')
    .slice(0, 3)
    .map(([key, value]) => {
      if (typeof value === 'number') {
        return `${key}: ${value.toFixed(3)}`;
      }

      return `${key}: ${String(value)}`;
    });
}

export function getApiErrorMessage(
  error: unknown,
  fallback: string
): string {
  if (typeof error === 'object' && error !== null) {
    const candidate = error as {
      response?: { data?: { detail?: string; message?: string } };
      message?: string;
    };

    return (
      candidate.response?.data?.detail ||
      candidate.response?.data?.message ||
      candidate.message ||
      fallback
    );
  }

  return fallback;
}

export function trainingStatusColor(
  status: ModelLabTrainingStatus
): ChipProps['color'] {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'RUNNING':
      return 'info';
    case 'QUEUED':
      return 'warning';
    case 'CANCELLED':
      return 'default';
    case 'DRAFT':
    default:
      return 'default';
  }
}

export function deploymentStatusColor(
  status: ModelLabDeploymentStatus
): ChipProps['color'] {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'PAUSED':
      return 'warning';
    case 'FAILED':
      return 'error';
    case 'DRAFT':
    default:
      return 'default';
  }
}

export function modelStageColor(
  stage: ModelLabVersionStage
): ChipProps['color'] {
  switch (stage) {
    case 'PRODUCTION':
      return 'success';
    case 'STAGING':
      return 'info';
    case 'ARCHIVED':
      return 'default';
    case 'DRAFT':
    default:
      return 'warning';
  }
}